import { Box, styled, Typography, type BoxProps } from '@mui/material';
import React from 'react';

import { SearchTableContext } from './SearchDialog';

const styles = {
  display: 'grid',
  gap: 2,
  alignItems: 'center',
  padding: 1
};

/**
 * Props for `SelectableTableRow`. They are forwarded to the row's MUI `Box`, except `sx`, which the row replaces
 * with its own grid layout so that every row lines up on the `gridTemplateColumns` of the enclosing `SearchTable`.
 * `style` is forwarded and overrides that layout, so do not set grid properties with it. A `ref` reaches the row's
 * `div`.
 */
export type SelectableTableRowProps = Omit<BoxProps, 'sx' | 'ref'> & {
  /**
   * @deprecated Ignored, so any value is accepted. The row sets its own grid layout so every row lines up with the
   * `SearchTable` columns; style the cells (`SelectableTableItem`) instead.
   */
  sx?: unknown;
};

/**
 * One row of a `SearchTable`: a grid row whose columns come from the table's `gridTemplateColumns`. Render one per
 * result inside the `SearchTable` row function, with a `SelectableTableItem` per cell.
 */
export const SelectableTableRow = React.forwardRef<HTMLDivElement, SelectableTableRowProps>(function SelectableTableRow(
  { children, ...props },
  ref
) {
  const gridTemplateColumns = React.useContext(SearchTableContext);
  return (
    <Box {...props} ref={ref} sx={{ ...styles, gridTemplateColumns }}>
      {children}
    </Box>
  );
});

/** One cell of a `SelectableTableRow`: a `Typography` in the theme's body text style. */
export const SelectableTableItem = styled(Typography, { shouldForwardProp: () => true })({});
